import { AccordionCardGroup } from '@ver-uds/react';
import { Alert, Form } from '@ver-uds/uswds-react';
import React, { ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EnrollmentType } from '../../../pages/Enrollment/EnrollmentData';
import { getEmployerAccessMethod, getEmployerWSAccessMethod } from '../../../pages/Enrollment/utils/AccessMethodUtils';
import EnrollmentStepperButtons from '../EnrollmentStepperButtons';
import EVerifyEmail from '../EVerifyEmail';
import { AccessMethodType } from './AccessMethodFormData';
import CorporateCard from './CorporateCard';
import EmployerAgentCard from './EmployerAgentCard';
import EmployerAgentWSCard from './EmployerAgentWSCard';
import EmployerCard from './EmployerCard';
import EmployerWSCard from './EmployerWSCard';

export type AccessMethodFormProps = {
  onSubmit({ accessMethod }: { accessMethod: AccessMethodType }): void;
  accessMethod?: AccessMethodType;
  enrollmentType: EnrollmentType;
};

const AccessMethodForm = ({ onSubmit, accessMethod, enrollmentType }: AccessMethodFormProps): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const formMethods = useForm<{ accessMethod: AccessMethodType }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      accessMethod,
    },
  });

  const { handleSubmit, errors } = formMethods;

  return (
    <Form className="maxw-none margin-bottom-3" onSubmit={handleSubmit(onSubmit)}>
      <h1 className="margin-bottom-4">Choose Account Access</h1>

      <p className="usa-intro">
        Please select the account access method that best describes how your company plans to use E-Verify. To learn
        more, click the arrow on the right of each option.
      </p>

      {errors.accessMethod ? (
        <Alert status="error" slim className="margin-y-3">
          <Alert.Text>{errors.accessMethod.message}</Alert.Text>
        </Alert>
      ) : null}

      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...formMethods}>
        <AccordionCardGroup className="margin-top-3 margin-bottom-neg-3">
          {
            [
              <EmployerCard key="employer" accessMethod={getEmployerAccessMethod(enrollmentType)} />,
              enrollmentType === EnrollmentType.INITIAL && <EmployerAgentCard key="agency" />,
              <EmployerWSCard key="employer-ws" accessMethod={getEmployerWSAccessMethod(enrollmentType)} />,
              enrollmentType === EnrollmentType.INITIAL && <EmployerAgentWSCard key="agency-ws" />,
              enrollmentType === EnrollmentType.INITIAL && <CorporateCard key="corporate" />,
            ] as ReactElement[]
          }
        </AccordionCardGroup>
      </FormProvider>

      <p className="margin-top-3 margin-bottom-4">
        Questions? We’re here to help! Email E-Verify support at <EVerifyEmail />.
      </p>

      <EnrollmentStepperButtons nextButtonType="submit" />
    </Form>
  );
};

export default AccessMethodForm;
